import React, { useState, useEffect } from 'react';
import { Calculator, Mail, MousePointer, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const EmailClickThroughRateCalculator = () => {
  const [emailsDelivered, setEmailsDelivered] = useState(10000);
  const [totalClicks, setTotalClicks] = useState(500);
  const [ctr, setCtr] = useState(0);

  useEffect(() => {
    calculateCTR();
  }, [emailsDelivered, totalClicks]);

  const calculateCTR = () => {
    const rate = (totalClicks / emailsDelivered) * 100;
    setCtr(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/email-click-through-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Email CTR Calculator: Measure Click-Through Rate | Free Tool</title>
        <meta name="description" content="Calculate your Email Click-Through Rate (CTR) instantly. Compare with industry benchmarks, get optimization tips, and improve your email engagement." />
        <meta name="keywords" content="email CTR calculator, click-through rate, email engagement metrics, CTA optimization, email marketing analytics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Email CTR Calculator: Boost Click-Through Rates" />
        <meta property="og:description" content="Calculate your Email Click-Through Rate instantly. Get actionable insights to improve your email engagement and conversions." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Email CTR Calculator: Boost Click-Through Rates" />
        <meta property="twitter:description" content="Calculate your Email Click-Through Rate instantly. Get actionable insights to improve your email engagement and conversions." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Email Click-Through Rate Calculator",
            "description": "Free calculator to measure and optimize email click-through rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Email CTR",
              "Industry Benchmarks",
              "CTA Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Email Click-Through Rate",
              "description": "The percentage of email recipients who click on one or more links contained in an email message."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Email Marketers, Digital Marketers, Content Creators"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Email Click-Through Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter number of emails delivered"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total number of clicks"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your CTR and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Email Click-Through Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Emails Delivered</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={emailsDelivered}
                  onChange={(e) => setEmailsDelivered(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Clicks</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MousePointer className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalClicks}
                  onChange={(e) => setTotalClicks(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {ctr}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Email Click-Through Rate (CTR)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Email Click-Through Rate (CTR) is {ctr}%. This means that {ctr}% of the delivered emails resulted in recipients clicking on a link.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 2.0-2.5%</li>
          <li>B2B Services: 2.5-3.0%</li>
          <li>Retail: 2.0-2.5%</li>
          <li>Technology: 2.5-3.0%</li>
          <li>Healthcare: 2.0-2.5%</li>
          <li>Nonprofits: 2.5-3.0%</li>
          <li>Education: 2.5-3.5%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">CTR Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create compelling content</li>
          <li>Use prominent CTA buttons</li>
          <li>Segment your email list</li>
          <li>Optimize for mobile</li>
          <li>Personalize content</li>
          <li>Test email layouts</li>
          <li>Add social sharing buttons</li>
          <li>Implement A/B testing</li>
          <li>Use clear value propositions</li>
          <li>Optimize button placement</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">CTA Best Practices</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Use action-oriented text</li>
          <li>Create urgency when appropriate</li>
          <li>Keep button text under 5 words</li>
          <li>Use contrasting colors</li>
          <li>Maintain white space around CTAs</li>
          <li>Test different button sizes</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Personalization increases CTR by 14%</li>
          <li>Mobile optimization boosts clicks by 15%</li>
          <li>Segmentation improves CTR by 100.95%</li>
          <li>A/B testing can lift CTR by 8-10%</li>
          <li>Clear CTAs improve clicks by 28%</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailClickThroughRateCalculator;
